import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import api from '../../api/axios';
import useGoogleDrive from '../../hooks/useGoogleDrive';

const GOOGLE_KEYS = [
  'google_client_id',
  'google_client_secret',
  'google_redirect_uri',
  'google_drive_service_account_json',
  'google_drive_backup_folder_id',
];

const GOOGLE_FIELD_INFO = {
  google_client_id: {
    label: 'Google Client ID',
    type: 'text',
    help: (
      <>
        El identificador público de tu aplicación OAuth.{' '}
        <strong>Cómo obtenerlo:</strong>{' '}
        Ve a <em>Google Cloud Console → APIs y servicios → Credenciales → Crear credenciales → ID de cliente de OAuth 2.0</em>.
        Copia el valor del campo <em>ID de cliente</em> (termina en <code>.apps.googleusercontent.com</code>).
      </>
    ),
  },
  google_client_secret: {
    label: 'Google Client Secret',
    type: 'password',
    help: (
      <>
        La clave secreta de tu aplicación OAuth.{' '}
        <strong>Cómo obtenerlo:</strong>{' '}
        En la misma pantalla de credenciales de Google Cloud Console, copia el valor del campo <em>Secreto de cliente</em>.
        Guárdalo con cuidado; no lo compartas públicamente.
      </>
    ),
  },
  google_redirect_uri: {
    label: 'Google Redirect URI',
    type: 'text',
    help: (
      <>
        La URL a la que Google redirige después de la autenticación.{' '}
        <strong>Cómo configurarlo:</strong>{' '}
        En Google Cloud Console, dentro de tu credencial OAuth, agrega esta URI en la sección{' '}
        <em>URI de redireccionamiento autorizados</em>. Debe ser exactamente:{' '}
        <code>https://tu-dominio.com/api/auth/google/callback</code>.
        En desarrollo local usa <code>http://localhost:8080/api/auth/google/callback</code>.
      </>
    ),
  },
  google_drive_service_account_json: {
    label: 'Google Drive Service Account (JSON)',
    type: 'textarea',
    help: (
      <>
        El contenido JSON de la cuenta de servicio para acceso a Google Drive sin OAuth de usuario.{' '}
        <strong>Cómo obtenerlo:</strong>{' '}
        Ve a <em>Google Cloud Console → IAM y administración → Cuentas de servicio → Crear cuenta de servicio</em>.
        Asigna el rol <em>Editor de Drive</em>. Luego en la pestaña <em>Claves</em>, crea una clave de tipo JSON.
        Pega aquí el contenido completo del archivo descargado.
      </>
    ),
  },
  google_drive_backup_folder_id: {
    label: 'Google Drive Backup Folder ID',
    type: 'text',
    help: (
      <>
        El ID de la carpeta de Google Drive donde se almacenarán los respaldos de la base de datos.{' '}
        <strong>Cómo obtenerlo:</strong>{' '}
        Abre la carpeta destino en Google Drive. El ID es la parte final de la URL:{' '}
        <code>https://drive.google.com/drive/folders/<strong>ESTE_ES_EL_ID</strong></code>.
        Asegúrate de compartir la carpeta con el correo de la cuenta de servicio.
      </>
    ),
  },
};

const SystemSettings = () => {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingGoogle, setSavingGoogle] = useState(false);
  const [form, setForm] = useState({});
  const [showSecret, setShowSecret] = useState({});
  const { connected, connect, disconnect } = useGoogleDrive();
  const [disconnecting, setDisconnecting] = useState(false);
  const [backingUp, setBackingUp] = useState(false);

  useEffect(() => {
    api.get('/settings/system').then(res => {
      setSettings(res.data);
      const initial = {};
      res.data.forEach(s => { initial[s.key] = s.value || ''; });
      setForm(initial);
    }).finally(() => setLoading(false));
  }, []);

  const generalSettings = settings.filter(s => !GOOGLE_KEYS.includes(s.key));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const settingsArray = generalSettings.map(s => ({ key: s.key, value: form[s.key] }));
      await api.put('/settings/system', { settings: settingsArray });
      toast.success('Configuración guardada');
    } catch {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveGoogle = async (e) => {
    e.preventDefault();
    setSavingGoogle(true);
    try {
      const settingsArray = GOOGLE_KEYS.map(key => ({ key, value: form[key] || '' }));
      await api.put('/settings/system', { settings: settingsArray });
      toast.success('Credenciales de Google guardadas');
    } catch {
      toast.error('Error al guardar credenciales');
    } finally {
      setSavingGoogle(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      await disconnect();
      toast.success('Drive desconectado');
    } catch {
      toast.error('Error al desconectar');
    } finally {
      setDisconnecting(false);
    }
  };

  const handleBackup = async () => {
    setBackingUp(true);
    try {
      const res = await api.post('/backup');
      toast.success(res.data.message);
    } catch {
      toast.error('Error al realizar el backup');
    } finally {
      setBackingUp(false);
    }
  };

  const toggleShow = (key) => setShowSecret(prev => ({ ...prev, [key]: !prev[key] }));

  if (loading) return <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>;

  return (
    <div className="container-fluid">
      <div className="page-header mb-4">
        <h4 className="mb-0">Configuración del Sistema</h4>
      </div>

      {/* Parámetros generales */}
      <div className="card mb-4">
        <div className="card-header">
          <h6 className="mb-0">Parámetros generales</h6>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            {generalSettings.map(setting => (
              <div key={setting.key} className="form-group row align-items-center">
                <label className="col-md-4 col-form-label">
                  <strong>{setting.label}</strong>
                  <br />
                  <small className="text-muted">{setting.key}</small>
                </label>
                <div className="col-md-3">
                  <input
                    type={setting.type}
                    className="form-control"
                    value={form[setting.key] || ''}
                    onChange={(e) => setForm({ ...form, [setting.key]: e.target.value })}
                    min={setting.type === 'number' ? 1 : undefined}
                  />
                </div>
                <div className="col-md-5">
                  {setting.key === 'stale_case_file_days' && (
                    <small className="text-muted">
                      Los expedientes activos sin ningún cambio por más de este número de días
                      se marcarán con alerta en la lista y en el dashboard.
                    </small>
                  )}
                </div>
              </div>
            ))}
            <hr />
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Guardando...' : 'Guardar configuración'}
            </button>
          </form>
        </div>
      </div>

      {/* Credenciales de Google API */}
      <div className="card mb-4">
        <div className="card-header d-flex align-items-center" style={{ gap: '8px' }}>
          <svg style={{ width: 20, height: 20, flexShrink: 0 }} viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          <h6 className="mb-0">Credenciales de Google API</h6>
        </div>
        <div className="card-body">
          <div className="alert alert-info py-2 mb-4" style={{ fontSize: '13px' }}>
            <strong>Antes de comenzar:</strong> Necesitas un proyecto en{' '}
            <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer">
              Google Cloud Console
            </a>{' '}
            con la <strong>Google Drive API</strong> habilitada. Ve a{' '}
            <em>APIs y servicios → Biblioteca</em>, busca "Google Drive API" y actívala.
          </div>

          <form onSubmit={handleSaveGoogle}>
            {GOOGLE_KEYS.map(key => {
              const info = GOOGLE_FIELD_INFO[key];
              const isPassword = info.type === 'password';
              const isTextarea = info.type === 'textarea';
              return (
                <div key={key} className="form-group mb-4">
                  <label className="font-weight-bold mb-1">{info.label}</label>
                  <small className="d-block text-muted mb-2" style={{ fontFamily: 'monospace', fontSize: '11px' }}>
                    {key.toUpperCase().replace(/_/g, '_')}
                  </small>

                  {isTextarea ? (
                    <textarea
                      className="form-control"
                      rows={6}
                      value={form[key] || ''}
                      onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                      placeholder='{ "type": "service_account", ... }'
                      style={{ fontFamily: 'monospace', fontSize: '12px' }}
                    />
                  ) : (
                    <div className="input-group">
                      <input
                        type={isPassword && !showSecret[key] ? 'password' : 'text'}
                        className="form-control"
                        value={form[key] || ''}
                        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                      />
                      {isPassword && (
                        <div className="input-group-append">
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => toggleShow(key)}
                            title={showSecret[key] ? 'Ocultar' : 'Mostrar'}
                          >
                            <i className={`ti-eye${showSecret[key] ? '-slash' : ''}`}></i>
                            {showSecret[key] ? ' Ocultar' : ' Mostrar'}
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="mt-2 p-2 rounded" style={{ background: '#f8f9fa', fontSize: '12px', color: '#555', lineHeight: '1.5' }}>
                    <i className="ti-info-alt mr-1" style={{ color: '#007bff' }}></i>
                    {info.help}
                  </div>
                </div>
              );
            })}

            <hr />
            <button type="submit" className="btn btn-primary" disabled={savingGoogle}>
              {savingGoogle ? 'Guardando...' : 'Guardar credenciales de Google'}
            </button>
          </form>
        </div>
      </div>

      {/* Integraciones */}
      <div className="card">
        <div className="card-header">
          <h6 className="mb-0">Integraciones</h6>
        </div>
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center flex-wrap" style={{ gap: '12px' }}>
            <div>
              <div className="d-flex align-items-center" style={{ gap: '10px' }}>
                <svg style={{ width: 24, height: 24, flexShrink: 0 }} viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <div>
                  <strong>Google Drive</strong>
                  <div className="small text-muted">
                    Almacenamiento de documentos y comprobantes de pago
                  </div>
                </div>
              </div>
            </div>
            <div className="d-flex align-items-center" style={{ gap: '10px' }}>
              {connected ? (
                <>
                  <span className="badge badge-success" style={{ fontSize: '12px', padding: '6px 10px' }}>
                    ✓ Conectado
                  </span>
                  <button className="btn btn-outline-secondary btn-sm" onClick={connect}>
                    <i className="ti-reload mr-1"></i> Reconectar
                  </button>
                  <button className="btn btn-outline-danger btn-sm" onClick={handleDisconnect} disabled={disconnecting}>
                    {disconnecting ? 'Desconectando...' : 'Desconectar'}
                  </button>
                </>
              ) : (
                <>
                  <span className="badge badge-secondary" style={{ fontSize: '12px', padding: '6px 10px' }}>
                    Sin conexión
                  </span>
                  <button className="btn btn-primary btn-sm" onClick={connect}>
                    <i className="ti-google mr-1"></i> Conectar Drive
                  </button>
                </>
              )}
            </div>
          </div>

          {connected && (
            <div className="alert alert-info mt-3 mb-0 py-2" style={{ fontSize: '13px' }}>
              <strong>Reconectar</strong> — úsalo si los archivos dejaron de subirse a Drive o si ves errores de token expirado.
            </div>
          )}
        </div>
      </div>

      {/* Respaldo */}
      <div className="card mt-4">
        <div className="card-header">
          <h6 className="mb-0">Respaldo de base de datos</h6>
        </div>
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center flex-wrap" style={{ gap: '12px' }}>
            <div>
              <strong>Backup a Google Drive</strong>
              <div className="small text-muted">
                Genera un dump de la base de datos y lo sube a Google Drive.
                Asegúrate de tener Drive conectado antes de ejecutar.
              </div>
            </div>
            <button
              className="btn btn-outline-primary"
              onClick={handleBackup}
              disabled={backingUp || !connected}
            >
              {backingUp ? (
                <><span className="spinner-border spinner-border-sm mr-2"></span>Respaldando...</>
              ) : (
                <><i className="ti-cloud-up mr-1"></i> Respaldar ahora</>
              )}
            </button>
          </div>
          {!connected && (
            <div className="alert alert-warning mt-3 mb-0 py-2" style={{ fontSize: '13px' }}>
              Conecta Google Drive primero para poder realizar el respaldo.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SystemSettings;
